<template>
	<div class="sidebar-tabbed-text-input" ref="sidebarTextInput">
		<b-tabs v-model="tabIndex" @input="handleTabChange">
			<b-tab v-for="(input, index) in data.tabs"
				   class="tab"
			       :key="input.key"
			       :title="input.tabLabel || input.label">
				<div class="col-12 container">
					<div v-if="tabIsBeingLoaded(index)" class="spinner-container">
						<erco-spinner class="spinner"
						              size="30px"
						              :isFixedColor="true">
						</erco-spinner>
					</div>
					<erco-text-input v-show="!tabIsBeingLoaded(index)"
					                 class="_input"
					                 label=""
					                 :ref="`input${index}`"
					                 :text="input.text"
					                 @input="handleInput(index, $event)"
					                 inputType="textarea"
					                 :resetable="true"
					                 :charlength="index === 0 && computedCharLength"
					                 @reset="handleReset(index)">
					</erco-text-input>
				</div>
			</b-tab>
		</b-tabs>
		<b-alert v-if="changes.length > 0 && !tabIsBeingLoaded(tabIndex)"
		         class="alert"
		         show
		         variant="warning">
		    <span v-if="changes.length === 1">
				Es liegt noch eine unbestätigte Änderung im Reiter {{ buildChangesStrList(changes) }} vor
		    </span>
		    <span v-else>
				Es liegen noch mehrere unbestätigte Änderungen in den Reitern {{ buildChangesStrList(changes) }} vor
		    </span>

		</b-alert>
	</div>
</template>

<script>
	import Vue from 'vue';

	import ErcoSpinner from '@/components/atoms/ErcoSpinner';
	import ErcoTextInput from '@/components/molecules/ErcoTextInput';

	export default {
		name: 'ErcoSidebarTabbedTextInputMolecule',

		props: {
			data: {
				type: Object,
				default: () => ({}),
			},
		},

		data() {
			return {
				innerTabIndex: null,
				tabsBeingLoaded: {},
				finishedTabFetching: {},
				changes: [],
			};
		},

		mounted() {
			Vue.nextTick(() => {
				const visibleInputEl =
					this.$refs[`input${this.tabIndex}`] &&
					this.$refs[`input${this.tabIndex}`][0];

				if (visibleInputEl) {
					visibleInputEl.focus();
				}
			});
		},

		computed: {
			tabIndex: {
				get() {
					return this.innerTabIndex ||
					       this.data.tabs.reduce((val, tab, index) =>
					           ((tab.tabKey === this.data.selectedTabKey) ? index : val), 0);
				},
				set(val) {
					this.innerTabIndex = val;
				},
			},
			computedCharLength() {
				return (this.data.charlength && this.data.charlength > -1) ? this.data.charlength : false;
			},
		},

		methods: {
			handleInput(index, newValue) {
				const key = this.data.tabs[index].key;
				const langKey = this.data.tabs[index].tabKey;

				const newSidebarContext = Object.assign({}, this.$store.state.sidebar.context);
				if (!newSidebarContext.value.sets) {
					this.$set(newSidebarContext.value, 'sets', {});
				}
				if (!newSidebarContext.value.sets[langKey]) {
					this.$set(newSidebarContext.value.sets, langKey, {});
				}
				this.$set(newSidebarContext.value.sets[langKey], key, newValue);

				this.$store.dispatch('updateSidebar', {
					context: newSidebarContext,
				});

				const currentLabel = this.data.tabs[index].tabLabel || this.data.tabs[index].label;
				if (!this.changes.includes(currentLabel)) {
					this.changes.push(currentLabel);
				}
			},

			handleReset(index) {
				this.handleInput(index, '');
			},

			tabIsBeingLoaded(index) {
				return index in this.tabsBeingLoaded;
			},

			handleApply() {
				this.changes = [];
			},

			buildChangesStrList(changes) {
				if (changes.length === 1) {
					return changes[0];
				}

				return changes.reduce((str, val, i) => {
					if (i === 0) {
						return val;
					}

					return (i < (changes.length - 1)) ?
							[str, val].join(', ') :
							[str, val].join(' und ');
				});
			},

			handleTabChange() {
				this.focusCurrentTabInput();
			},

			updateTextInputHeight() {
				/* Nach jedem erfolgten Tab-Fetching,
					sollte die Bestimmung der neuen Input- bzw. Textarea-Höhe von außen angestoßen werden */
				const inputEl =
					this.$refs[`input${this.tabIndex}`] && this.$refs[`input${this.tabIndex}`][0];

				if (inputEl && inputEl.handleInputHeight) {
					Vue.nextTick(() => {
						inputEl.handleInputHeight(true);
					});
				}

				this.focusCurrentTabInput();
			},

			focusCurrentTabInput() {
				/* Wenn die für den Tab relevanten Daten noch nicht geholt wurden,
					muss auch das Input-Feld nicht fokusiert werden */
				if (this.data.tabs[this.tabIndex].toBeFetched &&
					!this.finishedTabFetching[this.tabIndex]) {
					return;
				}

				const inputEl =
					this.$refs[`input${this.tabIndex}`] &&
					this.$refs[`input${this.tabIndex}`][0];

				if (inputEl && inputEl.focus) {
					Vue.nextTick(() => {
						inputEl.focus();
					});
				}
			},
		},

		watch: {
			tabIndex(newIndex) {
				if (this.data.tabs[newIndex].toBeFetched && !this.finishedTabFetching[newIndex]) {
					this.$set(this.tabsBeingLoaded, newIndex, true);

					const siteId = this.$store.state.sidebar.context.value.siteId;
					this.$store.dispatch(this.data.tabChangeAction, {
						lang: this.data.tabs[newIndex].tabKey,
						site: {
							PKM_Seite: siteId,
						},
					})
					.then((siteObject) => {
						this.$delete(this.tabsBeingLoaded, newIndex);
						this.$set(this.finishedTabFetching, newIndex, true);

						const newSidebarOutputData = Object.assign({}, this.data);
						newSidebarOutputData.tabs[newIndex].text = siteObject.items[this.data.tabs[newIndex].key].value || '';

						this.$store.dispatch('updateSidebar', {
							outputData: newSidebarOutputData,
						});
					})
					.catch(() => { /* TODO: Use (better) logging */ });
				} else {
					this.updateTextInputHeight();
				}
			},

			finishedTabFetching() {
				this.updateTextInputHeight();
			},
		},

		components: {
			ErcoSpinner,
			ErcoTextInput,
		},
	};
</script>

<style lang="scss">

	.sidebar-tabbed-text-input {
		padding: 0.5rem;
		padding-top: 1.5rem;

		& .tab {
			position: relative;
			min-height: 93px;

			& > .container {
				border: 1px solid rgba(221, 221, 221, 1);
				border-top: 0;

				& > .spinner-container {
					text-align: center;
					& > .spinner {
						margin: 1.5rem;
					}
				}
			}
		}

		& .alert {
			margin-top: 1rem;
		}
	}

</style>



// WEBPACK FOOTER //
// TabbedTextInput.vue?441da40e